import airportsData from "shared/config/airports/AllAirports/airports.json";
import { GTMEventParams } from "shared/services/GTM/types";
import {
  calculateDateDifference,
  getCurrentTime,
  transformDatesToGTM,
} from "shared/utils/dates";

export type FlightSearchIntent = {
  originAirport?: string;
  destinationAirport?: string;
  departureDate?: string[];
  returnDate?: string[];
  quantityOfAdults?: number;
  quantityOfChildrens?: number;
  quantityOfBabies?: number;
};

// The explicit `event` overrides the environment suffix added by sendGTMEvent:
// the GTM trigger behind the GA4 `search` event listens to `flightSearch`.
type FlightSearchEventData = GTMEventParams["flightSearch"] & {
  event: "flightSearch";
};

const getAirportData = (code: string) =>
  airportsData.find((item) => item.airport_code === code) ?? {
    country_code: "",
    city_name: "",
  };

const formatDates = (dates: string[], prefix: string) =>
  dates.map((date, index) => `${prefix}${index + 1}=${date}`).join("|");

export const getFlightSearchEventData = (
  searchIntent: FlightSearchIntent,
): FlightSearchEventData => {
  const origin = searchIntent.originAirport ?? "";
  const destination = searchIntent.destinationAirport ?? "";
  const departureDates = searchIntent.departureDate ?? [];
  const returnDates = searchIntent.returnDate ?? [];
  const adults = searchIntent.quantityOfAdults ?? 0;
  const children = searchIntent.quantityOfChildrens ?? 0;
  const babies = searchIntent.quantityOfBabies ?? 0;
  const originData = getAirportData(origin);
  const destinationData = getAirportData(destination);
  const isInternationalRoute =
    originData.country_code !== "BR" || destinationData.country_code !== "BR";
  // departure_yyyy_01, return_dd_01, ...: typed here because
  // transformDatesToGTM returns `any`, which would hide the checks below.
  const dateFields: Record<string, string> = transformDatesToGTM(
    departureDates,
    returnDates,
  );

  return {
    event: "flightSearch",
    sale_userid: null,
    facebook_userid: "",
    sale_source: null,
    sale_subsource: null,
    IATA_origin: origin,
    IATA_destination: destination,
    IATA_route: `${origin}-${destination}`,
    adults: `${adults}`,
    children: `${children}`,
    babies: `${babies}`,
    searchID: [
      `ao=${origin}`,
      `ad=${destination}`,
      formatDates(departureDates, "dl"),
      formatDates(returnDates, "dr"),
      `rt=${returnDates.length > 0 ? "1" : "0"}`,
      `a=${adults}`,
      `b=${babies}`,
      `c=${children}`,
      `et=${getCurrentTime()}`,
      "ip=",
      "us=",
      `if=${isInternationalRoute ? "1" : "0"}`,
      `co=${originData.country_code}`,
      `cd=${destinationData.country_code}`,
    ].join("|"),
    is_international_flight: destinationData.country_code !== "BR",
    country_origin: originData.country_code,
    country_destination: destinationData.country_code,
    ...dateFields,
    trip_duration: calculateDateDifference(departureDates, returnDates),
    is_multidates: departureDates.length > 1 || returnDates.length > 1,
    IATA_origin_autocomplete: originData.city_name,
    IATA_destination_autocomplete: destinationData.city_name,
  };
};
